import { OrganizationFilterSelectableEntities } from "@/lib/interfaces/organisation";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useUser } from "../../hooks/use-user";
import { UserRole } from "../../lib/interfaces/user";
import FilterIcon from "../../ui/components/icons/filter-icon";
import { Button } from "../../ui/components/ui/button";
import DataFetchParent from "../../ui/components/ui/data-fetch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/components/ui/select";
import AddOrganisationForm from "./root/add-organisation";
import OrganisationFetcher from "./root/organisation-fetcher";
import SearchInput from "../../ui/components/ui/search-input";
import { useSearchParams } from "react-router-dom";

const Organisations = () => {
  const [searchParams] = useSearchParams();
  const [showInvitations, setShowInvitations] = useState(false);
  const [showRequests, setShowRequests] = useState(searchParams.get("request-id") !== null);
  const [invitationCount, setInvitationCount] = useState(0);
  const [requestCount, setRequestCount] = useState(0);
  const [filterSelectableEntities, setFilterSelectableEntities] = useState<OrganizationFilterSelectableEntities>();
  const [initLoading, setInitLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.toString() || "status=ACTIVE&page=1");
  const { user } = useUser();

  useEffect(() => {
    // rerender
  }, [reload]);

  useEffect(() => {
    if (filterSelectableEntities) {
      setInitLoading(false);
    }
  }, [filterSelectableEntities]);

  return (
    <div className="flex flex-col gap-5 h-full">
      <div className="flex justify-between">
        {!showInvitations && !showRequests ? (
          <DataFetchParent
            isLoading={initLoading}
            data={filterSelectableEntities}
            renderElement={(data) => <Filters setSearchQuery={setSearchQuery} selectableEntities={data} />}
          />
        ) : (
          <div></div>
        )}
        {user?.userRole !== UserRole.User && (
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setShowInvitations(!showInvitations);
                setShowRequests(false);
              }}
              variant="outline"
            >
              {showInvitations
                ? "Kunden anzeigen"
                : invitationCount + (invitationCount === 1 ? " Einladung" : " Einladungen") + " anzeigen"}
            </Button>
            <Button
              onClick={() => {
                setShowRequests(!showRequests);
                setShowInvitations(false);
              }}
              variant="outline"
            >
              {showRequests
                ? "Kunden anzeigen"
                : requestCount + (requestCount === 1 ? " Kundenanfrage" : " Kundenanfragen") + " anzeigen"}
            </Button>
            <AddOrganisationForm setReload={setReload} />
          </div>
        )}
      </div>
      <OrganisationFetcher
        showInvitations={showInvitations}
        setInvitationCount={setInvitationCount}
        setFilterSelectableEntities={setFilterSelectableEntities}
        searchQuery={searchQuery}
        showRequests={showRequests}
        setRequestCount={setRequestCount}
      />
    </div>
  );
};

const Filters = ({
  setSearchQuery,
  selectableEntities,
}: {
  setSearchQuery: Dispatch<SetStateAction<string>>;
  selectableEntities: OrganizationFilterSelectableEntities;
}) => {
  const [activeFilter, setActiveFilter] = useState(true); // Default to ACTIVE
  const [inactiveFilter, setInactiveFilter] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get("textSearch") || "");
  const [firstRender, setFirstRender] = useState(true);

  const { user } = useUser();
  const [filtersChanged, setFiltersChanged] = useState(false);

  // Initialize state from URL parameters only on the first render
  useEffect(() => {
    if (!firstRender) return;

    const status = searchParams.get("status") || "ACTIVE";
    const textSearch = searchParams.get("textSearch") || "";
    const user = searchParams.get("assignedInternalUserId") || "";

    setActiveFilter(status === "ACTIVE");
    setInactiveFilter(status === "INACTIVE");
    setSearchText(textSearch);
    setSelectedUser(user);
    setFirstRender(false);
  }, [searchParams, firstRender]);

  useEffect(() => {
    if (firstRender) return;

    setFiltersChanged(true);
  }, [activeFilter, inactiveFilter, searchText, firstRender, selectedUser]);

  useEffect(() => {
    if (firstRender) return;

    const params = new URLSearchParams();

    const status = inactiveFilter ? "INACTIVE" : activeFilter ? "ACTIVE" : "ALL";

    params.set("status", status);
    if (searchText) params.set("textSearch", searchText);
    params.set("page", filtersChanged ? "1" : searchParams.get("page") || "1");
    if (selectedUser) params.set("assignedInternalUserId", selectedUser);

    // Update URL parameters only if they differ
    const queryString = params.toString();
    setSearchQuery(queryString);
    setSearchParams(params);
    setFiltersChanged(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersChanged, setSearchParams, setSearchQuery, firstRender, searchParams]);

  return (
    <div className="flex items-center gap-4">
      <SearchInput setSearchText={setSearchText} searchText={searchText} />
      <div className="flex gap-3">
        <Button
          variant={activeFilter ? "default" : "outline"}
          onClick={() => {
            setActiveFilter((prev) => !prev);
            setInactiveFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm "
        >
          <FilterIcon active={activeFilter} />
          Aktive Kunden
        </Button>
        <Button
          variant={inactiveFilter ? "default" : "outline"}
          onClick={() => {
            setInactiveFilter((prev) => !prev);
            setActiveFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm "
        >
          <FilterIcon active={inactiveFilter} />
          Inaktive Kunden
        </Button>
      </div>
      {user?.userRole !== UserRole.User && (
        <div className="relative">
          <Select
            onValueChange={(value) => {
              setSelectedUser(value);
            }}
            defaultValue={selectableEntities.userFilterUsed}
          >
            <SelectTrigger className="gap-3 h-10 items-center rounded-2xl px-3 py-2 border text-sm inline-flex justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground">
              {/* <ChevronDown size="18" /> */}
              <SelectValue placeholder="Mitarbeiter" />
            </SelectTrigger>
            <SelectContent className="cursor-pointer">
              <SelectItem value="ALL">Alle Mitarbeiter</SelectItem>
              {selectableEntities?.internalUsers.map((user) => (
                <SelectItem key={user._id} value={user._id}>
                  {user.value}
                </SelectItem>
              ))}
              <SelectItem value="NONE">Kein Mitarbeiter</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default Organisations;
